import React from 'react';
import { Delete } from 'lucide-react';

/**
 * Phase 17 — shared PIN UI primitives.
 *
 * Two pure-presentational components used by both PinSetup and PinUnlock
 * to keep the entry experience consistent.
 */

export function PinDots({ length = 4, filled = 0, error = false, testid = 'pin-dots' }) {
    return (
        <div data-testid={testid} style={{ display: 'flex', gap: 18, justifyContent: 'center' }}>
            {Array.from({ length }).map((_, i) => {
                const active = i < filled;
                return (
                    <span
                        key={`dot-${i}`}
                        style={{
                            width: 18,
                            height: 18,
                            borderRadius: 999,
                            background: active ? (error ? 'var(--nia-error)' : 'var(--nia-clay)') : 'transparent',
                            border: `2px solid ${error ? 'var(--nia-error)' : (active ? 'var(--nia-clay)' : 'var(--nia-border-strong)')}`,
                            transition: 'transform 180ms ease, background-color 180ms ease, border-color 180ms ease',
                            transform: active ? 'scale(1.08)' : 'scale(1)',
                        }}
                    />
                );
            })}
        </div>
    );
}

const KEYS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['', '0', 'del'],
];

export function PinKeypad({ onKey, disabled = false, testid = 'pin-keypad' }) {
    return (
        <div data-testid={testid} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 14, justifyContent: 'center' }}>
            {KEYS.flat().map((k, i) => {
                if (k === '') return <span key={`spacer-${i}`} style={{ width: 72, height: 72 }} aria-hidden />;
                const isDelete = k === 'del';
                return (
                    <button
                        key={`key-${k}-${i}`}
                        type="button"
                        disabled={disabled}
                        onClick={() => onKey(k)}
                        data-testid={`pin-key-${k}`}
                        aria-label={isDelete ? 'Delete' : `Digit ${k}`}
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 999,
                            border: '1px solid var(--nia-border)',
                            background: disabled ? 'var(--nia-muted)' : 'var(--nia-paper)',
                            color: 'var(--nia-ink)',
                            fontFamily: isDelete ? 'inherit' : "'Roboto Mono', ui-monospace, monospace",
                            fontSize: isDelete ? 22 : 26,
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            transition: 'transform 120ms ease, background-color 180ms ease',
                            boxShadow: '0 4px 12px rgba(20, 17, 15, 0.04)',
                        }}
                        onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.94)'; }}
                        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                        {isDelete ? <Delete size={20} /> : k}
                    </button>
                );
            })}
        </div>
    );
}
